/**
 * PaymentResult Component
 * 
 * Displays payment result after WebPay redirect.
 * Shows success page with credentials, error page with retry option,
 * or cancellation page based on payment status.
 * 
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  Copy,
  Check,
  ArrowLeft,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { confirmPayment, parseWebPayParams, getPaymentResultType } from '../services/paymentApi';
import { formatCLP } from '../utils/priceFormat';

/**
 * Moodle platform URL from environment variable
 */
const MOODLE_URL = import.meta.env.VITE_MOODLE_URL || 'http://localhost:8081';

/**
 * PaymentResult Component
 * 
 * Handles the payment confirmation flow after WebPay redirect:
 * 1. Parses URL parameters from WebPay
 * 2. Calls payment confirmation API
 * 3. Displays appropriate result page
 * 
 * @param {Object} props
 * @param {Function} [props.onRetry] - Callback to retry payment (navigate to enrollment)
 * @param {Function} [props.onGoHome] - Callback to go to home page
 */
export default function PaymentResult({ onRetry, onGoHome }) {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Process payment confirmation on mount
  useEffect(() => {
    async function processPayment() {
      try {
        // Parse WebPay parameters from URL
        const params = parseWebPayParams();
        const resultType = getPaymentResultType(params);

        console.log('Processing payment result:', { resultType, params });

        // If no valid parameters, show error
        if (resultType === 'unknown') {
          setError('No se encontraron parámetros de pago válidos.');
          setIsLoading(false);
          return;
        }

        // Confirm payment with backend
        const confirmResult = await confirmPayment(params);
        console.log('Payment confirmation result:', confirmResult);

        setResult(confirmResult);
      } catch (err) {
        console.error('Payment confirmation error:', err);
        setError(err.message || 'Error al confirmar el pago.');
      } finally {
        setIsLoading(false);
      }
    }

    processPayment();
  }, []);

  /**
   * Copy text to clipboard
   */
  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  /**
   * Navigate to the courses catalog
   */
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.href = '/cursos';
    }
  };

  /**
   * Navigate to home page
   */
  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  /**
   * Open Moodle platform
   */
  const handleOpenMoodle = () => {
    window.open(MOODLE_URL, '_blank');
  };

  // Loading state
  if (isLoading) {
    return (
      <ResultContainer>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Verificando Pago
          </h2>
          <p className="text-slate-400 max-w-md mx-auto">
            Estamos confirmando tu pago con WebPay. Por favor espera...
          </p>
        </motion.div>
      </ResultContainer>
    );
  }

  // Error state (no result)
  if (error && !result) {
    return (
      <ResultContainer>
        <ErrorPage
          message={error}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
        />
      </ResultContainer>
    );
  }

  // Render based on payment status
  const status = result?.status;

  if (status === 'approved') {
    return (
      <ResultContainer>
        <SuccessPage
          result={result}
          onOpenMoodle={handleOpenMoodle}
          onGoHome={handleGoHome}
          onCopy={handleCopy}
          copiedField={copiedField}
        />
      </ResultContainer>
    );
  }

  if (status === 'cancelled') {
    return (
      <ResultContainer>
        <CancelledPage
          result={result}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
        />
      </ResultContainer>
    );
  }

  if (status === 'timeout') {
    return (
      <ResultContainer>
        <TimeoutPage
          result={result}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
        />
      </ResultContainer>
    );
  }

  if (status === 'rejected') {
    return (
      <ResultContainer>
        <RejectedPage
          result={result}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
        />
      </ResultContainer>
    );
  }

  // Default error state
  return (
    <ResultContainer>
      <ErrorPage
        message={result?.errorMessage || 'Error desconocido'}
        onRetry={handleRetry}
        onGoHome={handleGoHome}
      />
    </ResultContainer>
  );
}

/**
 * Container wrapper for result pages
 */
function ResultContainer({ children }) {
  return (
    <section className="min-h-screen py-24 bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Success page with payment summary and credentials
 * Requirements: 6.1, 6.2, 6.3
 */
function SuccessPage({ result, onOpenMoodle, onGoHome, onCopy, copiedField }) {
  const { transactionDetails, credentials, enrolledCourses, buyOrder } = result;

  // Extract credentials from the API response
  const moodleUsername = credentials?.username;
  const temporaryPassword = credentials?.password;
  const isExistingUser = credentials?.isExistingUser;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      {/* Success Icon */}
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        ¡Pago Exitoso!
      </h2>
      <p className="text-slate-400 mb-8">
        Tu inscripción ha sido confirmada. Ya puedes acceder a tus cursos.
      </p>

      {/* Transaction Details */}
      {transactionDetails && (
        <div className="bg-white/5 rounded-2xl p-6 mb-6 text-left">
          <h3 className="text-lg font-semibold text-white mb-4">Detalles del Pago</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Orden de compra:</span>
              <span className="text-white font-mono">{buyOrder}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Código de autorización:</span>
              <span className="text-white font-mono">{transactionDetails.authorizationCode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Tarjeta:</span>
              <span className="text-white">**** {transactionDetails.cardNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Monto:</span>
              <span className="text-white font-semibold">{formatCLP(transactionDetails.amount)}</span>
            </div>
            {transactionDetails.installments > 1 && (
              <div className="flex justify-between">
                <span className="text-slate-400">Cuotas:</span>
                <span className="text-white">{transactionDetails.installments}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credentials */}
      {moodleUsername && (
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 mb-6 text-left border border-blue-400/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            {isExistingUser ? 'Acceso a tu Cuenta' : 'Tus Credenciales de Acceso'}
          </h3>

          {isExistingUser ? (
            <div>
              <p className="text-slate-300 mb-3">
                Ya tienes una cuenta en nuestra plataforma. Usa tus credenciales existentes para acceder.
              </p>
              <div className="mb-4">
                <label className="text-sm text-slate-400 block mb-1">Usuario:</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white/10 px-4 py-2 rounded-lg text-cyan-300 font-mono text-sm">
                    {moodleUsername}
                  </code>
                  <button
                    onClick={() => onCopy(moodleUsername, 'username')}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    aria-label="Copiar usuario"
                  >
                    {copiedField === 'username' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="text-sm text-slate-400 block mb-1">Usuario (email):</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white/10 px-4 py-2 rounded-lg text-cyan-300 font-mono text-sm">
                    {moodleUsername}
                  </code>
                  <button
                    onClick={() => onCopy(moodleUsername, 'username')}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    aria-label="Copiar usuario"
                  >
                    {copiedField === 'username' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>

              {temporaryPassword && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Contraseña temporal:</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white/10 px-4 py-2 rounded-lg text-cyan-300 font-mono text-sm">
                      {temporaryPassword}
                    </code>
                    <button
                      onClick={() => onCopy(temporaryPassword, 'password')}
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                      aria-label="Copiar contraseña"
                    >
                      {copiedField === 'password' ? (
                        <Check className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <Copy className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-amber-400 mt-2">
                    ⚠️ Recuerda cambiar tu contraseña en el primer inicio de sesión.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Enrolled Courses */}
      {enrolledCourses && enrolledCourses.length > 0 && (
        <div className="bg-white/5 rounded-2xl p-6 mb-6 text-left">
          <h3 className="text-lg font-semibold text-white mb-4">Cursos Inscritos</h3>
          <ul className="space-y-2">
            {enrolledCourses.map((course, index) => (
              <li key={index} className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                {course}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onOpenMoodle}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
        >
          <ExternalLink className="w-5 h-5" />
          Acceder al aula virtual
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>

      <p className="text-sm text-slate-500 mt-6">
        También recibirás un email con estos datos de acceso.
      </p>
    </motion.div>
  );
}

/**
 * Cancelled page when user aborts payment
 * Requirements: 6.6
 */
function CancelledPage({ result, onRetry, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <XCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Pago Cancelado
      </h2>
      <p className="text-slate-400 mb-8 max-w-md mx-auto">
        Has cancelado el proceso de pago. No se ha realizado ningún cargo a tu tarjeta.
      </p>

      {result?.buyOrder && (
        <p className="text-sm text-slate-500 mb-6">
          Referencia: {result.buyOrder}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRetry}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
          Intentar Nuevamente
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}

/**
 * Timeout page when payment times out
 */
function TimeoutPage({ result, onRetry, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <Clock className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Tiempo Agotado
      </h2>
      <p className="text-slate-400 mb-8 max-w-md mx-auto">
        El tiempo para completar el pago ha expirado. No se ha realizado ningún cargo a tu tarjeta.
      </p>

      {result?.buyOrder && (
        <p className="text-sm text-slate-500 mb-6">
          Referencia: {result.buyOrder}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRetry}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
          Intentar Nuevamente
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}

/**
 * Rejected page when payment is declined
 * Requirements: 6.4, 6.5
 */
function RejectedPage({ result, onRetry, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30">
        <XCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Pago Rechazado
      </h2>
      <p className="text-slate-400 mb-4 max-w-md mx-auto">
        {result?.rejectionReason || result?.errorMessage || 'Tu pago no pudo ser procesado.'}
      </p>
      <p className="text-sm text-slate-500 mb-8">
        Por favor verifica los datos de tu tarjeta o intenta con otro medio de pago.
      </p>

      {result?.buyOrder && (
        <p className="text-sm text-slate-500 mb-6">
          Referencia: {result.buyOrder}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRetry}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
          Intentar Nuevamente
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}

/**
 * Generic error page
 */
function ErrorPage({ message, onRetry, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30">
        <AlertCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Error
      </h2>
      <p className="text-slate-400 mb-8 max-w-md mx-auto">
        {message}
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRetry}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
          Intentar Nuevamente
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>
    </motion.div>
  );
}
